<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useCaptainIcon } from 'dashboard/composables/useCaptainIcon';

import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();
const { captainIconUrl, hasCaptainIcon, handleImageError } = useCaptainIcon();

// v6.4.25: Defaults ajustados para novas contas
const isUpdating = ref(false);
const bubbleEnabled = ref(false);
const adminOnly = ref(true);
const isInitialized = ref(false);

const accountId = computed(() => getters.getCurrentAccountId.value);

const currentAccount = computed(() => {
  return getters['accounts/getAccount'].value(accountId.value) || {};
});

// Initialize from store value
watch(
  () => currentAccount.value?.settings,
  newSettings => {
    if (!isUpdating.value && newSettings) {
      // v6.4.25: Default false — novas contas têm Bubble desativado por padrão
      bubbleEnabled.value = newSettings.copilot_bubble_enabled ?? false;
      // v6.4.25: Default true — novas contas têm Somente Admins ativado por padrão
      adminOnly.value = newSettings.copilot_bubble_admin_only ?? true;
      isInitialized.value = true;
    }
  },
  { immediate: true, deep: true }
);

const updateSetting = async (settingKey, newValue, oldValue, successKeyEnabled, successKeyDisabled) => {
  if (!isInitialized.value || isUpdating.value) return;

  isUpdating.value = true;
  try {
    const currentSettings = currentAccount.value?.settings || {};
    await store.dispatch('accounts/update', {
      settings: {
        ...currentSettings,
        [settingKey]: newValue,
      },
      options: { silent: true },
    });
    useAlert(
      newValue
        ? t(successKeyEnabled)
        : t(successKeyDisabled)
    );
  } catch (error) {
    // Rollback to previous value on error
    if (settingKey === 'copilot_bubble_enabled') {
      bubbleEnabled.value = oldValue;
    } else if (settingKey === 'copilot_bubble_admin_only') {
      adminOnly.value = oldValue;
    }
    console.error('[CopilotSettings] Failed to update:', settingKey, error);
    useAlert(t('INTEGRATION_SETTINGS.COPILOT_SETTINGS.ERROR'));
  } finally {
    isUpdating.value = false;
  }
};

// Watch for user changes to bubbleEnabled
watch(bubbleEnabled, async (newValue, oldValue) => {
  if (!isInitialized.value || isUpdating.value) return;
  if (newValue === oldValue) return;

  await updateSetting(
    'copilot_bubble_enabled',
    newValue,
    oldValue,
    'INTEGRATION_SETTINGS.COPILOT_SETTINGS.BUBBLE_TOGGLE.SUCCESS_ENABLED',
    'INTEGRATION_SETTINGS.COPILOT_SETTINGS.BUBBLE_TOGGLE.SUCCESS_DISABLED'
  );
});

// Watch for user changes to adminOnly
watch(adminOnly, async (newValue, oldValue) => {
  if (!isInitialized.value || isUpdating.value) return;
  if (newValue === oldValue) return;

  await updateSetting(
    'copilot_bubble_admin_only',
    newValue,
    oldValue,
    'INTEGRATION_SETTINGS.COPILOT_SETTINGS.ADMIN_ONLY_TOGGLE.SUCCESS_ENABLED',
    'INTEGRATION_SETTINGS.COPILOT_SETTINGS.ADMIN_ONLY_TOGGLE.SUCCESS_DISABLED'
  );
});

onMounted(() => {
  if (!currentAccount.value?.id) {
    store.dispatch('accounts/get');
  }
});
</script>

<template>
  <div class="mt-8">
    <h3 class="text-lg font-semibold text-n-slate-12 mb-4">
      {{ $t('INTEGRATION_SETTINGS.COPILOT_SETTINGS.TITLE') }}
    </h3>
    <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      <!-- Toggle Bubble Visibility -->
      <div
        class="flex flex-col flex-1 p-6 m-[1px] outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow"
      >
        <div class="flex items-start justify-between">
          <div class="flex h-12 w-12 mb-4">
            <div
              class="flex items-center justify-center w-full h-full rounded-md bg-gradient-to-br from-blue-500 to-cyan-600 text-white"
            >
              <img
                v-if="hasCaptainIcon"
                :src="captainIconUrl"
                alt="Copilot"
                class="size-7 object-contain"
                @error="handleImageError"
              />
              <i v-else class="i-woot-captain text-2xl" />
            </div>
          </div>
          <span
            v-tooltip="
              bubbleEnabled
                ? $t('INTEGRATION_APPS.STATUS.ENABLED')
                : $t('INTEGRATION_APPS.STATUS.DISABLED')
            "
            class="text-white p-0.5 rounded-full w-5 h-5 flex items-center justify-center"
            :class="bubbleEnabled ? 'bg-n-teal-9' : 'bg-n-slate-8'"
          >
            <i class="i-ph-check-bold text-sm" />
          </span>
        </div>
        <div class="flex flex-col m-0 flex-1">
          <div
            class="font-medium mb-2 text-n-slate-12 flex justify-between items-center"
          >
            <span class="text-base font-semibold">
              {{ $t('INTEGRATION_SETTINGS.COPILOT_SETTINGS.BUBBLE_TOGGLE.TITLE') }}
            </span>
            <ToggleSwitch v-model="bubbleEnabled" :disabled="isUpdating" />
          </div>
          <p class="text-n-slate-11 text-sm">
            {{
              $t('INTEGRATION_SETTINGS.COPILOT_SETTINGS.BUBBLE_TOGGLE.DESCRIPTION')
            }}
          </p>
        </div>
      </div>

      <!-- Toggle Admin Only -->
      <div
        class="flex flex-col flex-1 p-6 m-[1px] outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow"
        :class="{ 'opacity-50': !bubbleEnabled }"
      >
        <div class="flex items-start justify-between">
          <div class="flex h-12 w-12 mb-4">
            <div
              class="flex items-center justify-center w-full h-full rounded-md bg-gradient-to-br from-amber-500 to-orange-600 text-white"
            >
              <i class="i-lucide-shield-check text-2xl" />
            </div>
          </div>
          <span
            v-tooltip="
              adminOnly
                ? $t('INTEGRATION_APPS.STATUS.ENABLED')
                : $t('INTEGRATION_APPS.STATUS.DISABLED')
            "
            class="text-white p-0.5 rounded-full w-5 h-5 flex items-center justify-center"
            :class="adminOnly ? 'bg-n-teal-9' : 'bg-n-slate-8'"
          >
            <i class="i-ph-check-bold text-sm" />
          </span>
        </div>
        <div class="flex flex-col m-0 flex-1">
          <div
            class="font-medium mb-2 text-n-slate-12 flex justify-between items-center"
          >
            <span class="text-base font-semibold">
              {{ $t('INTEGRATION_SETTINGS.COPILOT_SETTINGS.ADMIN_ONLY_TOGGLE.TITLE') }}
            </span>
            <ToggleSwitch v-model="adminOnly" :disabled="isUpdating || !bubbleEnabled" />
          </div>
          <p class="text-n-slate-11 text-sm">
            {{
              $t('INTEGRATION_SETTINGS.COPILOT_SETTINGS.ADMIN_ONLY_TOGGLE.DESCRIPTION')
            }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
